/**
 * Customer 360 › Overview › Doors · Plans by address (the owner, 2026-10-22: two addresses, each its own round and its own
 * plan; one bill). Each active address with its round and whether it has a plan; an address without one gets
 * "Add a plan for this address": items, how often, from when. The same commands the restart uses (StartCustomerScreen):
 * api.create_subscription at that door, then api.set_subscription_status ACTIVE from the start date.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus } from 'lucide-react';
import { rpc, supabase } from '../../lib/supabase';
import { useSession } from '../../lib/session';
import { variantOptions } from '../../lib/variantOptions';
import { Button, Panel, PanelHeader, plainError } from '../../ui/primitives';
import { Field, Input, Select } from '../../ui/forms';
import { toast } from '../../ui/toast';
import { refreshCustomer } from './CustomerManage';
import type { Customer360 } from './customerDescriptor';

interface DoorRoute { locationId: string; code: string; label: string | null; isPrimary: boolean; status: string; ownRoute: string | null; route: string | null; hasPlan: boolean }
interface Variant { id: string; code: string; name: string; size_class: string | null }

function AddPlan({ entity, door, onDone }: { entity: Customer360; door: DoorRoute; onDone: () => void }) {
  const qc = useQueryClient();
  const variants = useQuery({ queryKey: ['variants-for-onboarding'], queryFn: async () => {
    const { data, error } = await supabase.from('product_variants').select('id,code,name,size_class').eq('status', 'ACTIVE').order('code');
    if (error) throw error; return data as Variant[];
  } });
  const earliest = useQuery({ queryKey: ['earliest-start'], queryFn: () => rpc<{ date: string; words: string }>('earliest_start') });
  const [items, setItems] = useState([{ variantId: '', quantity: 1 }]);
  const [pattern, setPattern] = useState('DAILY');
  const [start, setStart] = useState('');
  const [keys] = useState(() => [crypto.randomUUID(), crypto.randomUUID()]);
  const from = start || earliest.data?.date || '';
  const chosen = items.filter((i) => i.variantId && i.quantity > 0);
  const why = `A plan for ${door.label ?? door.code}, added on the customer's page`;
  const save = useMutation({
    mutationFn: async () => {
      const s = await rpc<{ id: string }>('create_subscription', { p_customer_id: entity.id, p_customer_location_id: door.locationId, p_start_date: from, p_schedule_pattern: pattern,
        p_lines: chosen.map((l) => ({ product_variant_id: l.variantId, quantity_units: l.quantity })), p_reason: why, p_idempotency_key: keys[0] });
      await rpc('set_subscription_status', { p_subscription_id: s.id, p_status: 'ACTIVE', p_effective_from: from, p_channel: 'OFFICE', p_reason: why, p_idempotency_key: keys[1] });
    },
    onSuccess: async () => {
      toast(`${door.label ?? door.code}: plan from ${from}, on round ${door.route ?? '—'}.`, { tone: 'ok', ms: 7000 });
      await qc.invalidateQueries({ queryKey: ['door-routes', entity.id] }); await refreshCustomer(qc, entity.id); onDone();
    },
  });
  const set = (i: number, p: Partial<(typeof items)[number]>) => setItems(items.map((x, j) => (j === i ? { ...x, ...p } : x)));
  return (
    <div className="grid gap-3 rounded-md border border-line p-3">
      <div className="grid gap-3 md:grid-cols-2">
        <Field label="How often"><Select value={pattern} onChange={(e) => setPattern(e.target.value)}
          options={[{ value: 'DAILY', label: 'Every day' }, { value: 'ALTERNATE', label: 'Alternate days' }]} /></Field>
        <Field label="First delivery" hint={earliest.data?.words}><Input type="date" min={earliest.data?.date} value={from} onChange={(e) => setStart(e.target.value)} /></Field>
      </div>
      {items.map((it, i) => (
        <div key={i} className="grid grid-cols-[minmax(0,1fr)_7rem_auto] items-end gap-2">
          <Field label={i === 0 ? 'Item' : ''}><Select value={it.variantId} onChange={(e) => set(i, { variantId: e.target.value })} placeholder="Choose an item"
            options={variantOptions(variants.data ?? [])} /></Field>
          <Field label={i === 0 ? 'Each time' : ''}><Input type="number" min={1} max={20} value={it.quantity} onChange={(e) => set(i, { quantity: Number(e.target.value) })} /></Field>
          <Button size="dense" disabled={items.length === 1} onClick={() => setItems(items.filter((_, j) => j !== i))}>Remove</Button>
        </div>))}
      <div><Button size="dense" onClick={() => setItems([...items, { variantId: '', quantity: 1 }])}><Plus size={14} aria-hidden /> Another item</Button></div>
      {save.error ? <p role="alert" className="text-small text-refused">{plainError((save.error as Error).message)}</p> : null}
      <div className="flex justify-end gap-2">
        <Button onClick={onDone}>Cancel</Button>
        <Button variant="primary" disabled={save.isPending || !chosen.length || !from} onClick={() => save.mutate()}>Start this plan</Button>
      </div>
    </div>
  );
}

export function DoorPlans({ entity }: { entity: Customer360 }) {
  const { can } = useSession();
  const q = useQuery({ queryKey: ['door-routes', entity.id], retry: false, queryFn: () => rpc<DoorRoute[]>('door_routes', { p_customer_id: entity.id }) });
  const [adding, setAdding] = useState<string | null>(null);
  const doors = (q.data ?? []).filter((d) => d.status === 'ACTIVE');
  if (doors.length < 2) return null;
  return (
    <Panel className="overflow-hidden">
      <PanelHeader title="Plans by address" note="Each address with a plan is delivered on its own round; one bill for all." />
      <ul className="divide-y divide-line">
        {doors.map((d) => (
          <li key={d.locationId} className="grid gap-2 px-4 py-3">
            <div className="flex flex-wrap items-center justify-between gap-2">
              <span><span className="font-medium">{d.label ?? d.code}</span> <span className="font-mono text-small text-ink-muted">{d.code}</span>{d.isPrimary ? <span className="ml-2 text-small text-brand">usual</span> : null}</span>
              <span className="text-small text-ink-muted">round <span className="font-mono">{d.route ?? '—'}</span>{d.ownRoute ? ' (its own)' : ''} · {d.hasPlan ? <span className="text-accepted">has a plan</span> : 'no plan: kept on record'}</span>
              {!d.hasPlan && can('subscription.manage') && adding !== d.locationId ? <Button size="dense" onClick={() => setAdding(d.locationId)}><Plus size={13} aria-hidden /> Add a plan for this address</Button> : null}
            </div>
            {adding === d.locationId ? <AddPlan entity={entity} door={d} onDone={() => setAdding(null)} /> : null}
          </li>))}
      </ul>
    </Panel>
  );
}
